import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Doctor } from "../../types/doctor.definition";
import { HealthcareFacilityStats } from "../../types/facility.definition";

export interface Activity {
  id: string;
  title: string;
  description: string;
  time: string;
  type: "appointment" | "checkin" | "cancellation" | "completion";
  appointmentId?: string;
  // Who booked a new appointment (MED-1648); the label is translated here.
  bookedByName?: string | null;
}

export interface ScheduleItem {
  id: string;
  title: string;
  time: string;
  endTime?: string;
  type: "appointment" | "meeting";
  appointmentId?: string;
  status?: string;
  reason?: string;
  patientName?: string;
  doctorName?: string;
  isActive?: boolean;
}

export interface Alert {
  id: string;
  title: string;
  description: string;
  type: "warning" | "success" | "error" | "info";
  severity: "low" | "medium" | "high";
}

interface DashboardState {
  stats: HealthcareFacilityStats | undefined;
  loading: boolean;
  error: string | null;
  lastFetched: number | null;
  facilityId: string | number | null;
  // Working doctors
  workingDoctors: Doctor[];
  workingDoctorsLoading: boolean;
  workingDoctorsError: string | null;
  // Today's schedule
  todaySchedule: ScheduleItem[];
  scheduleLoading: boolean;
  scheduleError: string | null;
  // Recent activity
  recentActivity: Activity[];
  activityLoading: boolean;
  activityError: string | null;
  // Alerts
  alerts: Alert[];
  alertsLoading: boolean;
  alertsError: string | null;
}

const initialState: DashboardState = {
  stats: undefined,
  loading: false,
  error: null,
  lastFetched: null,
  facilityId: null,
  workingDoctors: [],
  workingDoctorsLoading: false,
  workingDoctorsError: null,
  todaySchedule: [],
  scheduleLoading: false,
  scheduleError: null,
  recentActivity: [],
  activityLoading: false,
  activityError: null,
  alerts: [],
  alertsLoading: false,
  alertsError: null,
};

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    fetchStatsRequest: (
      state,
      _action: PayloadAction<{ facilityId: string | number }>
    ) => {
      state.loading = true;
      state.error = null;
    },
    fetchStatsSuccess: (
      state,
      action: PayloadAction<{
        stats: HealthcareFacilityStats;
        facilityId: string | number;
      }>
    ) => {
      state.stats = action.payload.stats;
      state.facilityId = action.payload.facilityId;
      state.lastFetched = Date.now();
      state.loading = false;
      state.error = null;
    },
    fetchStatsFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    invalidateStats: (state) => {
      // Clear stats to force refresh on next fetch
      state.stats = undefined;
      state.lastFetched = null;
      state.facilityId = null;
    },
    // Working doctors actions
    fetchWorkingDoctorsRequest: (
      state,
      _action: PayloadAction<{ facilityId: string | number }>
    ) => {
      state.workingDoctorsLoading = true;
      state.workingDoctorsError = null;
    },
    fetchWorkingDoctorsSuccess: (
      state,
      action: PayloadAction<{ doctors: Doctor[] }>
    ) => {
      state.workingDoctors = action.payload.doctors;
      state.workingDoctorsLoading = false;
      state.workingDoctorsError = null;
    },
    fetchWorkingDoctorsFailure: (state, action: PayloadAction<string>) => {
      state.workingDoctorsLoading = false;
      state.workingDoctorsError = action.payload;
    },
    // Today's schedule actions
    fetchTodayScheduleRequest: (
      state,
      _action: PayloadAction<{ facilityId: string | number }>
    ) => {
      state.scheduleLoading = true;
      state.scheduleError = null;
    },
    fetchTodayScheduleSuccess: (
      state,
      action: PayloadAction<{ schedule: ScheduleItem[] }>
    ) => {
      state.todaySchedule = action.payload.schedule;
      state.scheduleLoading = false;
      state.scheduleError = null;
    },
    fetchTodayScheduleFailure: (state, action: PayloadAction<string>) => {
      state.scheduleLoading = false;
      state.scheduleError = action.payload;
    },
    // Recent activity actions
    fetchRecentActivityRequest: (
      state,
      _action: PayloadAction<{ facilityId: string | number }>
    ) => {
      state.activityLoading = true;
      state.activityError = null;
    },
    fetchRecentActivitySuccess: (
      state,
      action: PayloadAction<{ activities: Activity[] }>
    ) => {
      state.recentActivity = action.payload.activities;
      state.activityLoading = false;
      state.activityError = null;
    },
    fetchRecentActivityFailure: (state, action: PayloadAction<string>) => {
      state.activityLoading = false;
      state.activityError = action.payload;
    },
    // Alerts actions
    fetchAlertsRequest: (
      state,
      _action: PayloadAction<{ facilityId: string | number }>
    ) => {
      state.alertsLoading = true;
      state.alertsError = null;
    },
    fetchAlertsSuccess: (state, action: PayloadAction<{ alerts: Alert[] }>) => {
      state.alerts = action.payload.alerts;
      state.alertsLoading = false;
      state.alertsError = null;
    },
    fetchAlertsFailure: (state, action: PayloadAction<string>) => {
      state.alertsLoading = false;
      state.alertsError = action.payload;
    },
    // Combined fetch for all dashboard data
    fetchDashboardDataRequest: (
      state,
      action: PayloadAction<{ facilityId: string | number }>
    ) => {
      // Remember which facility was requested, even if the fetch then fails, so
      // the dashboard does not re-request it in a loop (MED-1648).
      state.facilityId = action.payload.facilityId;
      state.loading = true;
      state.workingDoctorsLoading = true;
      state.scheduleLoading = true;
      state.activityLoading = true;
      state.alertsLoading = true;
      state.error = null;
      state.workingDoctorsError = null;
      state.scheduleError = null;
      state.activityError = null;
      state.alertsError = null;
    },
  },
});

export const {
  fetchStatsRequest,
  fetchStatsSuccess,
  fetchStatsFailure,
  invalidateStats,
  fetchWorkingDoctorsRequest,
  fetchWorkingDoctorsSuccess,
  fetchWorkingDoctorsFailure,
  fetchTodayScheduleRequest,
  fetchTodayScheduleSuccess,
  fetchTodayScheduleFailure,
  fetchRecentActivityRequest,
  fetchRecentActivitySuccess,
  fetchRecentActivityFailure,
  fetchAlertsRequest,
  fetchAlertsSuccess,
  fetchAlertsFailure,
  fetchDashboardDataRequest,
} = dashboardSlice.actions;

export default dashboardSlice.reducer;
